import { layerDepth, type OrderedProp } from "daedalus-core";
import type { PropView } from "daedalus-renderer-phaser";

/**
 * The things the scene draws, one prop view per compiled layer they stand on (P1.1-T7, ADR-071 known
 * difference 1): each view draws at that layer's depth, so a thing sorts with the props of its own layer as
 * the editor sorts the prop it was lowered from. Views are made on first use and kept; a layer that holds no
 * thing after a sync keeps an empty view. The scene lists every view with its prop views, so the occluder
 * fade and the cover report see things wherever they draw. Each thing carries its appearance's tie key, so it
 * falls in one order with the props of its layer (P1.6-T42).
 */
export class ThingViews {
  private readonly views = new Map<number, PropView>();
  private readonly owners = new Map<string, PropView>();
  private readonly groups = new Map<number, OrderedProp[]>();

  /**
   * @param create Makes a prop view and lists it with the scene's views under a key naming the layer.
   */
  public constructor(private readonly create: (key: string) => PropView) {}

  /**
   * Brings the views in line with the things in view: each thing drawn in the view of its layer, every other
   * view emptied.
   *
   * @param things Each thing's instance and the compiled index of the layer it draws at.
   * @param band The Players band, for the layers' depths.
   * @throws Error when a thing's frame is missing from the asset manifest.
   */
  public sync(things: readonly { readonly instance: OrderedProp; readonly layer: number }[], band: number): void {
    this.groups.clear();
    for (const thing of things) {
      let group = this.groups.get(thing.layer);
      if (group === undefined) {
        group = [];
        this.groups.set(thing.layer, group);
      }
      group.push(thing.instance);
    }
    this.owners.clear();
    for (const [layer, group] of this.groups) {
      let view = this.views.get(layer);
      if (view === undefined) {
        view = this.create(`things:${layer}`);
        this.views.set(layer, view);
      }
      for (const instance of group) {
        this.owners.set(instance.id, view);
      }
    }
    for (const [layer, view] of this.views) {
      view.sync(this.groups.get(layer) ?? [], layerDepth(layer, band), true, null);
    }
  }

  /**
   * Shows a frame on a drawn thing without changing it, as a transition plays over it.
   *
   * @param id The thing's prop id.
   * @param frame The frame id to show.
   * @throws Error when the frame is missing from the asset manifest.
   */
  public showFrame(id: string, frame: string): void {
    this.owners.get(id)?.showFrame(id, frame);
  }

  /**
   * Removes every drawn thing.
   *
   * @param band The Players band.
   */
  public clear(band: number): void {
    this.sync([], band);
  }
}
